import { supabase } from '../supabaseClient';
import { isEventOver } from '../utils/eventCutoff';

/**
 * A festival stays on the Sets index until it is over - events.ends_at, 6 AM after its last day
 * (migration 134, utils/eventCutoff). The date floor only keeps the query small: nothing that started
 * more than four days ago is still on.
 */
const DAYS_AFTER_START = 4;

/**
 * Every upcoming festival that has a lineup, soonest first — the Sets index.
 *
 * Two counts come back with each row: how many lineup rows it has, and how many
 * of those carry a set time. A festival publishes its lineup months out and its
 * set times days out (of the ten festivals live on 2026-09-22, only Outside
 * Lands had times), so the index says which one you are about to open rather
 * than pretending an un-timed lineup is a schedule.
 *
 * `is_festival` is the pipeline's own classification, not the venue tier — a
 * club night at a festival-tier venue is not a festival here (see utils/eventType).
 */
const SET_INDEX_SELECT =
  // has_lineup is an empty inner embed: it keeps only festivals with at least one lineup row IN the
  // query, so the limit below counts festivals with lineups. Filtering lineupCount > 0 after a limit
  // let lineup-less festivals use up the 60 and cut real ones off the end.
  'event_id, event_name, display_name, display_name_override, event_date, event_datetime, ends_at, multi_day, timezone, hero_image_url, card_image_url, venues(name, city), lineup:event_artists(count), timed:event_artists(count), has_lineup:event_artists!inner()';

const toIndexRow = ({ has_lineup: _hasLineup, ...row }) => ({
  ...row,
  venues: Array.isArray(row.venues) ? (row.venues[0] ?? null) : row.venues,
  lineupCount: row.lineup?.[0]?.count ?? 0,
  timedCount: row.timed?.[0]?.count ?? 0,
});

export async function fetchFestivalSetIndex() {
  const since = new Date();
  since.setDate(since.getDate() - DAYS_AFTER_START);

  const { data, error } = await supabase
    .from('events')
    .select(SET_INDEX_SELECT)
    .eq('is_festival', true)
    .neq('status', 'cancelled')
    // The two gates the feed RPC applies, mirrored, so a festival on this index is one
    // whose event page exists: a day/pass variant folded into a parent is not its own
    // entry, and an event_type='festival' row stays hidden until festival_ready is set.
    .is('festival_parent_event_id', null)
    .or('event_type.not.ilike.festival,festival_ready.is.true')
    .gte('event_date', since.toISOString().slice(0, 10))
    .not('timed.set_start_time', 'is', null)
    .order('event_date', { ascending: true })
    .limit(60);
  if (error) throw error;

  return (data ?? []).map(toIndexRow).filter((row) => row.lineupCount > 0 && !isEventOver(row));
}

/**
 * The festivals you went to that are over, latest first - the Sets index's Past (Sam, 2026-10-27: past sets matter
 * once ratings come). Only ones you were going to, so the list is yours and stays short; same gates as the index.
 * Only festivals that got set times: a lineup announced without them (CRSSD Spring, Coachella 2026 - six names
 * each) has nothing to look back on.
 */
export async function fetchPastFestivalSets(goingEventIds) {
  if (!goingEventIds?.length) return [];
  const { data, error } = await supabase
    .from('events')
    .select(SET_INDEX_SELECT)
    .in('event_id', goingEventIds)
    .eq('is_festival', true)
    .neq('status', 'cancelled')
    .is('festival_parent_event_id', null)
    .or('event_type.not.ilike.festival,festival_ready.is.true')
    .lte('event_date', new Date().toISOString().slice(0, 10))
    .not('timed.set_start_time', 'is', null)
    .order('event_date', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(toIndexRow).filter((row) => row.timedCount > 0 && isEventOver(row));
}
